import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  ChartNoAxesCombined,
  CheckSquare,
  Columns2,
  LayoutPanelTop,
  LockKeyhole,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import { motion } from 'motion/react';
import { usePageTitle } from '../lib/usePageTitle';
import { ApiError, publicApi } from '../lib/apiClient';
import './product.css';
import { textError, emailError } from '../lib/publicFormValidation';

type DemoErrors = Partial<Record<'name' | 'email' | 'company', string>>;

const NAME_PATTERN = /^[A-Za-z\s]+$/;

const nameFormatError = (value: string): string | undefined => {
  if (value && !NAME_PATTERN.test(value)) {
    return 'Name can only contain letters.';
  }
  return undefined;
};

const DemoRequestModal = ({ onClose }: { onClose: () => void }) => {
  const firstInputRef = useRef<HTMLInputElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submittingRef = useRef(false);
  const [errors, setErrors] = useState<DemoErrors>({});

  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    firstInputRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !submittingRef.current) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocusedRef.current?.focus();
    };
  }, [onClose]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submittingRef.current) return;
    const form = event.currentTarget;
    const formData = new FormData(form);
    const name = String(formData.get('name') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const company = String(formData.get('company') ?? '').trim();
    const nextErrors: DemoErrors = {};

    nextErrors.name = textError(name, 'Name', 2, 100) || nameFormatError(name);
    if (emailError(email, 255)) {
      nextErrors.email = 'Enter a valid work email.';
    }
    nextErrors.company = textError(company, 'Company name', 2, 255);

    setErrors(nextErrors);
    const firstInvalidField = (['name', 'email', 'company'] as const).find(
      (field) => nextErrors[field],
    );
    if (firstInvalidField) {
      window.requestAnimationFrame(() => {
        (form.elements.namedItem(firstInvalidField) as HTMLElement | null)?.focus();
      });
      return;
    }

    const optionalError =
      textError(String(formData.get('teamSize') ?? ''), 'Team size', 0, 50) ||
      textError(String(formData.get('note') ?? ''), 'Problem description', 0, 10000);
    if (optionalError) {
      setSubmitError(optionalError);
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await publicApi.submitProductDemo({
        name,
        email,
        company,
        teamSize: String(formData.get('teamSize') ?? '').trim(),
        note: String(formData.get('note') ?? '').trim(),
      });
      setSubmitted(true);
    } catch (error) {
      setSubmitError(
        error instanceof ApiError
          ? error.message
          : 'Could not send the demo request. Please try again.',
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div
      className="product-modal-overlay"
      role="presentation"
      onMouseDown={(event) =>
        event.target === event.currentTarget && !submittingRef.current && onClose()
      }
    >
      <div
        className="product-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-demo-modal-title"
        aria-describedby="product-demo-modal-description"
      >
        <button
          type="button"
          className="product-modal-close"
          onClick={onClose}
          disabled={submitting}
          aria-label="Close"
        >
          ✕
        </button>
        {submitted ? (
          <div className="product-form-success">
            <span aria-hidden="true">✓</span>
            <h3 id="product-demo-modal-title">Request received.</h3>
            <p id="product-demo-modal-description">
              We&apos;ll be in touch within one business day to set up a time to walk you through
              QuantumDesk.
            </p>
          </div>
        ) : (
          <>
            <span className="product-modal-eyebrow">// QuantumDesk</span>
            <h3 id="product-demo-modal-title">Request a demo</h3>
            <p className="product-modal-sub" id="product-demo-modal-description">
              See the platform our own recruiters use to run hiring end to end.
            </p>
            <form onSubmit={handleSubmit} noValidate>
              <div className="product-form-grid">
                <div className="product-form-row">
                  <label className="product-form-field">
                    <span>Your name</span>
                    <input
                      ref={firstInputRef}
                      name="name"
                      type="text"
                      required
                      placeholder="Jordan Patel"
                      aria-invalid={Boolean(errors.name)}
                      aria-describedby={errors.name ? 'product-demo-name-error' : undefined}
                    />
                    {errors.name && <small id="product-demo-name-error">{errors.name}</small>}
                  </label>
                  <label className="product-form-field">
                    <span>Work email</span>
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="jordan@company.com"
                      aria-invalid={Boolean(errors.email)}
                      aria-describedby={errors.email ? 'product-demo-email-error' : undefined}
                    />
                    {errors.email && <small id="product-demo-email-error">{errors.email}</small>}
                  </label>
                </div>
                <div className="product-form-row">
                  <label className="product-form-field">
                    <span>Company</span>
                    <input
                      name="company"
                      type="text"
                      required
                      placeholder="Company name"
                      aria-invalid={Boolean(errors.company)}
                      aria-describedby={errors.company ? 'product-demo-company-error' : undefined}
                    />
                    {errors.company && (
                      <small id="product-demo-company-error">{errors.company}</small>
                    )}
                  </label>
                  <label className="product-form-field">
                    <span>Team size</span>
                    <select name="teamSize" defaultValue="1–10">
                      <option>1–10</option>
                      <option>11–50</option>
                      <option>51–200</option>
                      <option>201–1000</option>
                      <option>1000+</option>
                    </select>
                  </label>
                </div>
                <label className="product-form-field">
                  <span>
                    What are you hoping to solve? <i>(optional)</i>
                  </span>
                  <textarea
                    name="note"
                    placeholder="Current ATS, hiring volume, what's not working today..."
                  />
                </label>
                {submitError && (
                  <p className="product-form-note" role="alert">
                    {submitError}
                  </p>
                )}
                <button
                  type="submit"
                  className="product-form-submit"
                  disabled={submitting}
                  aria-busy={submitting}
                >
                  {submitting ? 'Submitting…' : 'Request a demo →'}
                </button>
                <p className="product-form-note">
                  We&apos;ll reach out within one business day to find a time.
                </p>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const funnelSteps = [
  ['Career site', 'Candidate-facing pages, built in'],
  ['Source & screen', 'Pipeline, profiles, scorecards'],
  ['Agency partners', 'Referrals tracked in one place'],
  ['Approve & offer', 'Per-approver chains, no email chases'],
  ['Onboard', 'First-day readiness, tracked'],
] as const;

const features: ReadonlyArray<{
  icon: LucideIcon;
  title: string;
  copy: string;
}> = [
  {
    icon: LayoutPanelTop,
    title: 'Career Sites & Candidate Pages',
    copy: "Build a career site and role-specific candidate pages without hiring a designer — the same engine behind QuantumHire's own candidate briefing pages.",
  },
  {
    icon: UserRound,
    title: 'Agency & Vendor Management',
    copy: 'A scoped portal for every referral agency you work with — the roles opened to them, a submit-a-candidate form, and live referral-to-hire conversion tracking, all in one place instead of a dozen inboxes.',
  },
  {
    icon: Columns2,
    title: 'Sourcing & Pipeline',
    copy: 'Data-driven candidate profiles with skills, experience, scorecards, and a stage-aware activity timeline — plus bulk actions and reject-with-reason for when a role moves fast.',
  },
  {
    icon: CheckSquare,
    title: 'Approvals, Offers & Onboarding',
    copy: `Per-approver chains for roles and signed offers, plus a hire-to-first-day checklist the moment someone signs — no email chases, and "they accepted" doesn't quietly turn into a laptop that isn't ready.`,
  },
  {
    icon: ChartNoAxesCombined,
    title: 'Live Analytics',
    copy: 'Conversion funnel and candidate-source breakdown computed from real pipeline data — including which agencies actually convert, not just who sends the most resumes.',
  },
  {
    icon: LockKeyhole,
    title: 'Secure, Without the Bloat',
    copy: 'Roles & permissions, a real audit log, and SSO/SCIM when you need it — the compliance basics a growing team actually asks for, without an enterprise sales cycle to get them.',
  },
];

const capabilities = [
  'Career Sites',
  'Agency Management',
  'Sourcing & Pipeline',
  'Approvals & Offers',
  'Live Analytics',
  'Onboarding',
] as const;

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.18 },
  transition: { duration: 0.7 },
};

export const ProductPage = () => {
  const [isDemoOpen, setIsDemoOpen] = useState(false);
  usePageTitle('QuantumDesk — QuantumHire Product');

  return (
    <main className="product-page">
      <header className="product-hero">
        <div className="product-hero-inner">
          <h1>
            Not another ATS. A hiring platform built to move the needle for SMBs and mid-market
            teams.
          </h1>
          <p>
            QuantumDesk<span className="product-tm">™</span> covers the entire hiring funnel in one
            platform — the career site candidates land on, the agencies you manage, the pipeline
            your team works, and the offer that gets signed. Built by a team that runs recruiting
            operations every day, not a generic ATS vendor guessing at the workflow.
          </p>
          <button
            type="button"
            className="product-primary-button"
            onClick={() => setIsDemoOpen(true)}
          >
            Request a demo →
          </button>
        </div>
      </header>

      <section className="product-section product-funnel-section">
        <div className="product-section-heading">
          <h2>The whole funnel, not just the pipeline</h2>
          <p>
            Most ATS tools start at &quot;candidate applies.&quot; QuantumDesk starts before that —
            and doesn&apos;t stop until they&apos;re onboarded.
          </p>
        </div>
        <div className="product-funnel" aria-label="QuantumDesk hiring funnel">
          {funnelSteps.map(([title, copy], index) => (
            <div className="product-funnel-group" key={title}>
              <motion.article
                className="product-funnel-step"
                {...reveal}
                transition={{ duration: 0.7, delay: index * 0.08 }}
              >
                <span>{index + 1}</span>
                <h3>{title}</h3>
                <p>{copy}</p>
              </motion.article>
              {index < funnelSteps.length - 1 && (
                <span className="product-funnel-arrow" aria-hidden="true">
                  →
                </span>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="product-section product-section-tint">
        <div className="product-section-heading">
          <h2>Everything a growing hiring team actually needs</h2>
          <p>
            Not a stripped-down ATS with a chatbot bolted on, and not enterprise-tool bloat
            you&apos;ll never touch — the parts of the funnel that actually move the needle for a
            team that&apos;s outgrown spreadsheets.
          </p>
        </div>
        <div className="product-feature-grid">
          {features.map(({ icon: Icon, title, copy }, index) => (
            <motion.article
              className="product-feature-card"
              key={title}
              {...reveal}
              transition={{ duration: 0.7, delay: index * 0.08 }}
            >
              <span className="product-feature-icon">
                <Icon aria-hidden="true" />
              </span>
              <h3>{title}</h3>
              <p>{copy}</p>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="product-section">
        <div className="product-section-heading product-story-heading">
          <h2>Why we built it ourselves</h2>
        </div>
        <motion.div className="product-origin" {...reveal}>
          <p>
            Most hiring platforms are built by software teams who&apos;ve never actually run a
            recruiting desk — and most are priced and packaged for enterprises with dedicated ops
            teams to configure them. QuantumDesk is built the other way around: by the same team
            sourcing, screening, and placing candidates for SMBs and mid-market companies every day,
            for the workflow we actually needed and couldn&apos;t find off the shelf.
          </p>
          <p>
            That&apos;s why it covers the funnel most ATS tools ignore — a real career-site builder
            instead of a bare job-post form, a real agency portal instead of a generic
            vendor-management stub — while staying simple enough for a lean team to actually run
            without a dedicated administrator.
          </p>
        </motion.div>
        <div className="product-chip-row" aria-label="QuantumDesk capabilities">
          {capabilities.map((capability) => (
            <span key={capability}>{capability}</span>
          ))}
        </div>
      </section>

      <section className="product-final-cta">
        <h2>Want to see QuantumDesk in action?</h2>
        <p>Get a walkthrough of the platform our own recruiters use to run hiring end to end.</p>
        <button
          type="button"
          className="product-primary-button"
          onClick={() => setIsDemoOpen(true)}
        >
          Request a demo →
        </button>
      </section>
      {isDemoOpen && <DemoRequestModal onClose={() => setIsDemoOpen(false)} />}
    </main>
  );
};
